import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Stack,
  Tabs,
  TabPanel,
  TabPanels,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useLocation } from 'contexts/LocationContext';
import CInput from 'components/CInput';
import CButton from 'components/CButton';
import ImageUpload from 'components/ImageUpload';
import DaysTimeTable from 'components/DaysTimeTable';
import RangeColorTable from 'components/RangeColorTable';

const rangeColor = [
  { range: { from: 0, to: 0 }, color_code: '#FFFF00' },
  { range: { from: 0, to: 0 }, color_code: '#00FF00' },
  { range: { from: 0, to: 0 }, color_code: '#ADD8E6' },
  { range: { from: 0, to: 0 }, color_code: '#00008B' },
  { range: { from: 0, to: 0 }, color_code: '#FF0000' },
]

export default function NewLocation() {
  const { createLocation, getSingleLocation, updateLocation } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [validated, setValidated] = useState(false);
  const [locationDetail, setLocationDetail] = useState({
    name: '',
    description: '',
    logo: null,
    range: rangeColor,
    color_code: '#f97316',
    is_public: false,
    is_bookable: false,
    weekly_schedule: {
      monday: { start_time: null, end_time: null },
      tuesday: { start_time: null, end_time: null },
      wednesday: { start_time: null, end_time: null },
      thursday: { start_time: null, end_time: null },
      friday: { start_time: null, end_time: null },
      saturday: { start_time: null, end_time: null },
      sunday: { start_time: null, end_time: null },
    },
  });

  useEffect(() => {
    if (params.id) {
      fetchSingleLocation();
    } else {
      resetLocationDetails();
    }
  }, [params.id]);

  const fetchSingleLocation = async () => {
    try {
      const location = await getSingleLocation(params.id);
      if (location) {
        setLocationDetail({
          ...location.location,
          range: location.location.range.length > 0 ? location.location.range : rangeColor,
        });
      }
    } catch (error) {
      console.error('Error fetching location:', error);
    }
  };

  const resetLocationDetails = () => {
    setLocationDetail({
      name: '',
      description: '',
      logo: null,
      range: rangeColor,
      color_code: '#f97316',
      is_public: false,
      is_bookable: false,
      weekly_schedule: {
        monday: { start_time: null, end_time: null },
        tuesday: { start_time: null, end_time: null },
        wednesday: { start_time: null, end_time: null },
        thursday: { start_time: null, end_time: null },
        friday: { start_time: null, end_time: null },
        saturday: { start_time: null, end_time: null },
        sunday: { start_time: null, end_time: null },
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (!locationDetail.name || !locationDetail.description || !locationDetail.range) {
      return;
    }

    for (const memberRange of locationDetail.range) {
      const { from, to } = memberRange.range;

      if (from === "" || to === "" || from === null || to === null || (Number(from) > Number(to))) {
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('name', locationDetail.name);
      formData.append('description', locationDetail.description);
      formData.append('range', JSON.stringify(locationDetail.range));
      formData.append('is_public', locationDetail.is_public ? 1 : 0);
      formData.append('is_bookable', locationDetail.is_bookable ? 1 : 0);
      formData.append('weekly_schedule', JSON.stringify(locationDetail.weekly_schedule));
      formData.append('logo', locationDetail.logo || '');

      const response = params.id
        ? await updateLocation(params.id, formData)
        : await createLocation(formData);

      if (response && !response.error) {
        navigate('/admin/locations');
      }
    } catch (error) {
      console.error('Error submitting location:', error);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="start"
      top="0"
      pt={{ sm: '125px', lg: '75px' }}
      position="relative"
      w="100%"
    >
      <Tabs w="100%">
        <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
          <TabPanel w="100%" p="0px" mx="auto">
            <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
              Location Info
            </Text>
            <form onSubmit={handleSubmit}>
              <Flex direction="row" w="100%" justify="space-between">
                <Stack direction="column" spacing="20px" w={{ base: "100%", md: "48%" }} mr='30px'>
                  <CInput
                    label="Name"
                    type="text"
                    value={locationDetail.name}
                    onChange={(value) =>
                      setLocationDetail({ ...locationDetail, name: value })
                    }
                    required={true}
                    validated={validated}
                    placeholder="e.g., Esthera"
                    w="100%"
                    mb="8px !important"
                    disabled={true}
                  />
                  <CInput
                    label="Address"
                    type="textarea"
                    value={locationDetail.description}
                    onChange={(value) =>
                      setLocationDetail({
                        ...locationDetail,
                        description: value,
                      })
                    }
                    required={true}
                    validated={validated}
                    placeholder="Enter Location"
                    w="100%"
                    mb="8px !important"
                  />
                  <CInput
                    label="Public"
                    type="switch"
                    colorScheme="orange"
                    value={locationDetail.is_public}
                    onChange={(value) =>
                      setLocationDetail({
                        ...locationDetail,
                        is_public: value,
                      })
                    }
                    validated={validated}
                    w="100%"
                    mb="8px !important"
                  />
                  <CInput
                    label="Bookable"
                    type="switch"
                    colorScheme="orange"
                    value={locationDetail.is_bookable}
                    onChange={(value) =>
                      setLocationDetail({
                        ...locationDetail,
                        is_bookable: value,
                      })
                    }
                    validated={validated}
                    w="100%"
                    mb="8px !important"
                  />
                  <ImageUpload
                    label="Logo"
                    existingImage={locationDetail.logo && locationDetail.logo instanceof File ? URL.createObjectURL(locationDetail.logo) : locationDetail.logo}
                    onImageSelect={(file) => setLocationDetail({ ...locationDetail, logo: file })}
                  />
                </Stack>
                <Box w={{ base: "100%", md: "48%" }} borderLeft='1px' borderRight='1px' borderColor='gray.400'>
                  <RangeColorTable
                    rows={locationDetail.range}
                    setRows={(updatedRange) =>
                      setLocationDetail({ ...locationDetail, range: updatedRange })
                    }
                  />
                </Box>
                <Box w={{ base: "100%", md: "48%" }}>
                  <DaysTimeTable
                    schedule={locationDetail.weekly_schedule}
                    onScheduleChange={(updatedSchedule) =>
                      setLocationDetail((prevDetails) => ({
                        ...prevDetails,
                        weekly_schedule: updatedSchedule,
                      }))
                    }
                  />
                </Box>
              </Flex>
              <Flex justify="flex-end" align="center" mt="44px" gap="3">
                <Button
                  variant="darkBrand"
                  fontSize="sm"
                  borderRadius="10px"
                  w={{ base: '128px', md: '148px' }}
                  h="46px"
                  type="button"
                  bg="gray"
                  color="white"
                  _hover={{ bg: 'gray.600' }}
                  onClick={() => navigate('/admin/locations')}
                >
                  Cancel
                </Button>
                <CButton
                  w={{ base: '128px', md: '148px' }}
                  h="46px"
                  type="submit"
                >
                  Submit
                </CButton>
              </Flex>
            </form>

          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}
